import type React from 'react';
import { useState } from 'react';
import { Button, Tooltip } from 'antd';
import clsx from 'clsx';
import { useDispatch } from 'react-redux';
import { Setting } from '@/lib/reactIconly';
import ModalTestersMode from '@/domains/Auth/components/ModalTestersMode';
import getTestModeParams from '@/lib/getTestModeParams';
import { useRefreshUserMutation } from '@/domains/Auth/api/authApiSlice';
import { useGetUsersQuery } from '@/domains/RBAC/api/usersApiSlice';
import Config from '@/configs/appConfig';
import { FEATURE_SHOW_TEST_MODE, Permissions } from '@/const';
import { hasRights } from '@/components/HasRights/HasRights';
import { refreshUserData } from '@/domains/Auth/features/authSlice';
import styles from './TestButton.module.css';

const TestButton: React.FC = () => {
  const dispatch = useDispatch();
  const [isActiveTestersModeModal, setToggleTestersModeModal] = useState(false);
  const testModeParams = getTestModeParams();
  const developerId = testModeParams?.developer_id ?? null;
  const isActiveTestMode = Boolean(developerId);
  const [refreshUser, { isLoading: isRefreshUserLoading }] = useRefreshUserMutation();

  const { data: { data: users = [] } = { data: [] }, isFetching: isLoadingUsers } = useGetUsersQuery(
    {
      queryParams: 'limit=0',
      customHeaders: { 'x-api-developer': Config.VITE_TEST_MODE_TOKEN },
    },
    { skip: !FEATURE_SHOW_TEST_MODE || !hasRights([Permissions.ADMINISTRATION.USERS.USERS_LIST.VIEW_PAGE]) },
  );

  const toggleTestersModeModal = () => {
    setToggleTestersModeModal(!isActiveTestersModeModal);
  };

  const handleRefreshUserData = () => {
    refreshUser({})
      .unwrap()
      .then((res) => {
        dispatch(refreshUserData(res));
        toggleTestersModeModal();
      });
  };

  const handleStopTestMode = () => {
    localStorage.removeItem('test_mode_params');
    handleRefreshUserData();
  };

  const handleApplyTestMode = (values: { developer_id: number }) => {
    const requestParams = { is_developer: Config.VITE_TEST_MODE_TOKEN, developer_id: values.developer_id };

    localStorage.setItem('test_mode_params', JSON.stringify(requestParams));
    handleRefreshUserData();
  };

  return (
    <>
      <ModalTestersMode
        isActiveModal={isActiveTestersModeModal}
        onCancel={toggleTestersModeModal}
        initialValues={{ developer_id: developerId ?? null }}
        formErrors={[]}
        data={users ?? []}
        isActiveTestMode={isActiveTestMode}
        handleApplyTestMode={handleApplyTestMode}
        handleStopTestMode={handleStopTestMode}
        isSubmitting={isRefreshUserLoading}
      />
      {!isActiveTestersModeModal && FEATURE_SHOW_TEST_MODE && (
        <Tooltip placement="left" title="Test mode">
          <div className={styles.testSettings}>
            <Button
              shape="circle"
              size="large"
              icon={<Setting />}
              loading={isLoadingUsers || isRefreshUserLoading}
              disabled={isLoadingUsers || isRefreshUserLoading}
              className={clsx({
                [styles.progressBtnCircle]: isActiveTestMode,
                [styles.warningBtnCircle]: !isActiveTestMode,
              })}
              onClick={toggleTestersModeModal}
            />
          </div>
        </Tooltip>
      )}
    </>
  );
};

export default TestButton;
